/**
 * Mounts the dashboard shell once (spec §5.9, D-TBD-15): a `div.dash-layer` inserted **before** `#overlay`, so the
 * pause menu (inside `#overlay`) draws over it when Esc opens the menu on top. Hidden, not unmounted, while closed,
 * so opening is one attribute change and one render. Renders run on snapshot change only.
 */
import { Dashboard } from './Dashboard'
import type { DashboardConfig } from './config'
import { mountReact } from '../strategic/mount'
import type { DashCommandPort } from './registry'
import type { DashNavPort } from './focus'
import type { DashSnapshot, DashView } from '../../features/dashboard/controller'
import './dashboard.css'

export interface DashViewOptions {
  /** `#overlay` (the pause menu's layer). */
  readonly anchor: HTMLElement
  readonly cfg: DashboardConfig
  readonly port: DashCommandPort
  readonly nav: DashNavPort
  onTab(index: number): void
  onSection(index: number): void
}

export interface DashDomView extends DashView {
  readonly el: HTMLElement
  dispose(): void
}

export function createDashView(o: DashViewOptions): DashDomView {
  const doc = o.anchor.ownerDocument
  const el = doc.createElement('div')
  el.className = 'dash-layer'
  el.hidden = true
  if (o.anchor.parentNode === null) o.anchor.appendChild(el)
  else o.anchor.before(el)
  const mount = mountReact(el, 'dash-layer', false)
  return {
    el,
    show(): void {
      el.hidden = false
    },
    hide(): void {
      el.hidden = true
    },
    render(s: DashSnapshot): void {
      mount.render(
        <Dashboard
          cfg={o.cfg}
          tab={s.tab}
          section={s.section}
          depth={s.depth}
          focus={s.focus}
          time={s.time}
          ctx={s.ctx}
          badges={s.badges}
          overview={s.overview}
          port={o.port}
          nav={o.nav}
          onTab={o.onTab}
          onSection={o.onSection}
        />,
      )
    },
    dispose(): void {
      mount.unmount()
      el.remove()
    },
  }
}
